/**
 * LeadHelper - A utility class for saving and retrieving UTM tags,
 * referrer, and A/B test values in cookies for tracking and marketing purposes.
 */
class LeadHelper {
  /**
   * Constructor checks for UTM tags, referrer, and A/B test parameters in the URL
   * and stores them in cookies if available.
   */
  constructor() {
    const utmTags = this.getUtmTagsFromUrl();
    if (Object.keys(utmTags).length > 0) {
      LeadHelper.setCookie('utm_tags', JSON.stringify(utmTags));
    }

    const currentUrl = window.location.href;
    const referrer = document.referrer;
    // this is a list of pages that are not considered as referrer
    // rintajouppi.fi is the main page
    // https://j-rinta-jouppi-admin.stage.crasman.cloud/ is the staging page
    // vcap.me is localhost
    // this is used to prevent setting the referrer cookie if the user is on the same page
    const notOnPage = ['rintajouppi.fi', 'https://j-rinta-jouppi-admin.stage.crasman.cloud/', 'vcap.me'];
    if (referrer && !currentUrl.includes(referrer) && !notOnPage.some(page => referrer.includes(page))) {
      LeadHelper.setCookie('referrer', referrer);
    }

    const convert = this.getConverInfo();
    if (convert) {
      LeadHelper.setCookie('convert', convert);
    }

    this.genericFormInteraction();
  }

  /**
   * Sets a cookie with the given name, value, and expiration time.
   * @param {string} name - The name of the cookie.
   * @param {string} value - The value to store in the cookie.
   * @param {number} expirationDays - Number of days before the cookie expires.
   * @static
   */
  static setCookie(name, value, expirationDays = 30) {
    const date = new Date();
    date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
    // Set the cookie with the specified name, value, expiration date, and path
    const expires = 'expires=' + date.toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/`;
  }

  /**
   * Retrieves a cookie value by name.
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} - The cookie value or null if not found.
   * @static
   */
  static getCookie(name) {
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    const nameEQ = name + '=';
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.startsWith(nameEQ)) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  }

  /**
   * Extracts UTM parameters from the URL.
   * @returns {Object} - An object containing UTM key-value pairs.
   */
  getUtmTagsFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmTags = {};
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('utm_')) {
        utmTags[key] = value;
      }
    }
    return utmTags;
  }

  /**
   * Returns the saved A/B test variant from the cookie.
   * @returns {string|null} - The A/B test value or null if not set.
   */
  getConverInfo() {
    const dataLayer = window.dataLayer || [];
    const convert = dataLayer
      .filter(item => item.event?.includes('convert-trigger-experience-'))
      .map(item => `convert: ${item.experienceId}_${item.variation_name}`);

    return convert.join(' | ');
  }

  static parseAsiaKoskeeFromCookies(keysToCheck = ['convert', 'utm_tags', 'referrer']) {
    const asiaKoskeeArray = [];
    keysToCheck.forEach(key => {
      const value = LeadHelper.getCookie(key);
      if (!value) return;
      if (key === 'utm_tags') {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const formatted = Object.entries(parsed)
              .map(([k, v]) => `${k} : ${v}`)
              .join(' | ');
            asiaKoskeeArray.push(formatted);
          }
        } catch (e) {
          // JSON parse fail – no action needed
        }
      } else if (key === 'convert') {
        asiaKoskeeArray.push(value);
      } else {
        asiaKoskeeArray.push(`${key} : ${value}`);
      }
      // Clear the cookie
      LeadHelper.setCookie(key, '', -1);
    });
    return asiaKoskeeArray;
  }

  /**
   * Sets a cookie with the form name when a user interacts with a form.
   * This is done by adding an event listener to each form on the page.
   */
  genericFormInteraction() {
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
      const formIdOrName = form.getAttribute('name') || form.getAttribute('id');

      // Skip if already tracked or no identifier
      if (!formIdOrName) return;

      const handler = () => {
        LeadHelper.setCookie('formName', '', -1);
        LeadHelper.setCookie('formName', formIdOrName, 30);
        form.removeEventListener('input', handler);
      };

      form.addEventListener('input', handler, { once: true });
    });
  }
}

module.exports = LeadHelper;
